import SEO, { schemas } from '../components/SEO';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, ArrowLeft, Calendar, MapPin, User, Car } from 'lucide-react';
import { createBooking, createGuestBooking } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STEPS = ['Passenger Info', 'Trip Details', 'Mobility & Extras', 'Confirm'];

const SERVICE_TYPES = [
  { value: 'nemt', label: '🚑 Non-Emergency Medical', desc: 'Doctor visits, dialysis, therapy' },
  { value: 'wheelchair', label: '♿ Wheelchair Accessible', desc: 'Full mobility support' },
  { value: 'senior_assisted', label: '👴 Senior Assisted', desc: 'Extra care for elderly passengers' },
  { value: 'hospital_discharge', label: '🏥 Hospital Discharge', desc: 'Post-hospital safe transfer' },
  { value: 'special_needs', label: '💙 Special Needs', desc: 'Compassionate specialized care' },
  { value: 'recurring', label: '🔁 Recurring', desc: 'Scheduled ongoing transport' },
];

const INIT = {
  passenger_name: '', passenger_phone: '', passenger_email: '',
  pickup_address: '', pickup_suite: '', pickup_city: '',
  destination_address: '', destination_suite: '', destination_city: '',
  contact_at_destination_name: '', contact_at_destination_phone: '',
  appointment_date: '', appointment_time: '',
  ride_type: 'one_time', service_type: 'nemt',
  needs_wheelchair: false, needs_walker: false, needs_stretcher: false,
  special_instructions: '',
  recurrence_pattern: '', recurrence_end_date: '',
};

export default function BookingPage({ isDashboard }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const fn = user ? createBooking : createGuestBooking;
      await fn(form);
      setDone(true);
      toast.success('Booking submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit booking.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-20">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3 font-heading">Booking Received!</h2>
          <p className="text-gray-600 mb-2">Thank you, <strong>{form.passenger_name}</strong>!</p>
          <p className="text-gray-500 text-sm mb-8">Your ride request has been submitted. Our team will review it and contact you at <strong>{form.passenger_phone}</strong> to confirm the details.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              <button onClick={() => navigate('/dashboard/rides')} className="btn-primary">View My Rides</button>
            ) : (
              <Link to="/" className="btn-primary">Back to Home</Link>
            )}
            <button onClick={() => { setDone(false); setStep(0); setForm(INIT); }} className="btn-outline">Book Another Ride</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isDashboard ? '' : 'min-h-screen bg-gray-50 py-12'}>
      {!isDashboard && (
        <>
          <SEO
            title="Book a Ride — Schedule Your Medical Transportation"
            description="Book your non-emergency medical transportation with MDS Transportation LLC. Easy online scheduling — wheelchair, senior, hospital discharge, and recurring rides in Fort Wayne, Indianapolis and nearby counties."
            path="/book"
            schema={schemas.book}
          />
          <div className="max-w-3xl mx-auto px-4 mb-8 text-center">
            <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Get Started</span>
            <h1 className="text-4xl font-bold text-gray-900 mt-2 mb-3 font-heading">Book a Ride</h1>
            <p className="text-gray-600">Fill out the form below to schedule your non-emergency medical transportation.</p>
          </div>
        </>
      )}

      <div className="max-w-3xl mx-auto px-4">
        {/* Step indicators */}
        <div className="flex items-center mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${i < step ? 'bg-green-500 text-white' : i === step ? 'bg-purple-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i < step ? '✓' : i + 1}
              </div>
              <span className={`ml-2 text-xs font-medium hidden sm:block ${i === step ? 'text-purple-700' : 'text-gray-400'}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${i < step ? 'bg-green-400' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="card border border-gray-100 shadow-sm">
          {/* Step 0: Passenger Info */}
          {step === 0 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><User size={18} className="text-purple-700" /></div>
                <div><h2 className="text-xl font-bold text-gray-900 font-heading">Passenger Information</h2><p className="text-sm text-gray-500">Who needs the ride?</p></div>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="label">Full Name *</label>
                  <input className="input-field" required placeholder="John Doe" value={form.passenger_name} onChange={e => set('passenger_name', e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label">Phone Number *</label>
                    <input className="input-field" required placeholder="+1 (555) 000-0000" value={form.passenger_phone} onChange={e => set('passenger_phone', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Email Address</label>
                    <input className="input-field" type="email" placeholder="patient@example.com" value={form.passenger_email} onChange={e => set('passenger_email', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="label">Service Type *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                    {SERVICE_TYPES.map(s => (
                      <label key={s.value} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${form.service_type === s.value ? 'border-purple-700 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}>
                        <input type="radio" name="service_type" value={s.value} checked={form.service_type === s.value} onChange={e => set('service_type', e.target.value)} className="mt-1" />
                        <div>
                          <p className="font-medium text-sm text-gray-900">{s.label}</p>
                          <p className="text-xs text-gray-500">{s.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Trip Details */}
          {step === 1 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><MapPin size={18} className="text-purple-700" /></div>
                <div><h2 className="text-xl font-bold text-gray-900 font-heading">Trip Details</h2><p className="text-sm text-gray-500">Where and when?</p></div>
              </div>
              <div className="space-y-5">
                <div className="grid sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-2">
                    <label className="label">Pickup Address *</label>
                    <input className="input-field" required placeholder="123 Main Street" value={form.pickup_address} onChange={e => set('pickup_address', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Suite / Apt # <span className="text-gray-400 font-normal">(optional)</span></label>
                    <input className="input-field" placeholder="e.g. Apt 4B, Suite 200" value={form.pickup_suite} onChange={e => set('pickup_suite', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="label">Pickup City</label>
                  <input className="input-field" placeholder="Indianapolis" value={form.pickup_city} onChange={e => set('pickup_city', e.target.value)} />
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <div className="grid sm:grid-cols-3 gap-5">
                    <div className="sm:col-span-2">
                      <label className="label">Destination Address *</label>
                      <input className="input-field" required placeholder="456 Medical Drive" value={form.destination_address} onChange={e => set('destination_address', e.target.value)} />
                    </div>
                    <div>
                      <label className="label">Suite / Apt # <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input className="input-field" placeholder="e.g. Room 3A, Suite 100" value={form.destination_suite} onChange={e => set('destination_suite', e.target.value)} />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="label">Destination City</label>
                    <input className="input-field" placeholder="Indianapolis" value={form.destination_city} onChange={e => set('destination_city', e.target.value)} />
                  </div>
                </div>

                {/* Contact person at destination */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-blue-900 mb-0.5">Contact Person at Destination <span className="font-normal text-blue-500">(optional)</span></p>
                    <p className="text-xs text-blue-600">A nurse, receptionist, or family member who can be reached at the drop-off location.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Contact Name</label>
                      <input className="input-field" placeholder="e.g. Nurse Williams" value={form.contact_at_destination_name} onChange={e => set('contact_at_destination_name', e.target.value)} />
                    </div>
                    <div>
                      <label className="label">Contact Phone</label>
                      <input className="input-field" type="tel" placeholder="+1 (555) 000-0000" value={form.contact_at_destination_phone} onChange={e => set('contact_at_destination_phone', e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label">Appointment Date *</label>
                    <input type="date" className="input-field" required min={new Date().toISOString().split('T')[0]} value={form.appointment_date} onChange={e => set('appointment_date', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Appointment Time *</label>
                    <input type="time" className="input-field" required value={form.appointment_time} onChange={e => set('appointment_time', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="label">Ride Type *</label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[{ v: 'one_time', l: 'One-Time Ride' }, { v: 'recurring', l: 'Recurring Ride' }, { v: 'hospital_discharge', l: 'Hospital Discharge' }].map(({ v, l }) => (
                      <label key={v} className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer text-sm font-medium transition-colors ${form.ride_type === v ? 'border-purple-700 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-700'}`}>
                        <input type="radio" name="ride_type" value={v} checked={form.ride_type === v} onChange={e => set('ride_type', e.target.value)} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                {form.ride_type === 'recurring' && (
                  <div className="grid sm:grid-cols-2 gap-5 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div>
                      <label className="label">Recurrence Pattern</label>
                      <select className="input-field" value={form.recurrence_pattern} onChange={e => set('recurrence_pattern', e.target.value)}>
                        <option value="">Select pattern</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">End Date</label>
                      <input type="date" className="input-field" value={form.recurrence_end_date} onChange={e => set('recurrence_end_date', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Mobility needs */}
          {step === 2 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><Car size={18} className="text-purple-700" /></div>
                <div><h2 className="text-xl font-bold text-gray-900 font-heading">Mobility & Special Needs</h2><p className="text-sm text-gray-500">Help us prepare the right vehicle and assistance.</p></div>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="label">Mobility Equipment Required</label>
                  <div className="grid sm:grid-cols-3 gap-3 mt-1">
                    {[['needs_wheelchair', '♿ Wheelchair'], ['needs_walker', '🦯 Walker'], ['needs_stretcher', '🛏️ Stretcher']].map(([key, label]) => (
                      <label key={key} className={`flex items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-colors ${form[key] ? 'border-purple-700 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}>
                        <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="w-4 h-4 text-purple-700" />
                        <span className="text-sm font-medium text-gray-800">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">Special Instructions</label>
                  <textarea className="input-field min-h-[120px] resize-none" placeholder="Anything our driver should know? (e.g., patient uses oxygen tank, needs extra time to board, building has no elevator, etc.)" value={form.special_instructions} onChange={e => set('special_instructions', e.target.value)} />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  <strong>Note:</strong> For urgent transportation needs or questions, please call us directly at <strong>+1 (317) 982-1959</strong>. Our team is available 24/7.
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><CheckCircle size={18} className="text-purple-700" /></div>
                <div><h2 className="text-xl font-bold text-gray-900 font-heading">Confirm Your Booking</h2><p className="text-sm text-gray-500">Review the details below before submitting.</p></div>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Passenger',     value: form.passenger_name },
                  { label: 'Phone',         value: form.passenger_phone },
                  { label: 'Email',         value: form.passenger_email || '—' },
                  { label: 'Service Type',  value: SERVICE_TYPES.find(s => s.value === form.service_type)?.label },
                  { label: 'Pickup',        value: [form.pickup_address, form.pickup_suite, form.pickup_city].filter(Boolean).join(', ') },
                  { label: 'Destination',   value: [form.destination_address, form.destination_suite, form.destination_city].filter(Boolean).join(', ') },
                  ...(form.contact_at_destination_name ? [{ label: 'Destination Contact', value: [form.contact_at_destination_name, form.contact_at_destination_phone].filter(Boolean).join(' · ') }] : []),
                  { label: 'Date',          value: (() => { const d = new Date(form.appointment_date + 'T12:00:00'); return isNaN(d.getTime()) ? form.appointment_date : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); })() },
                  { label: 'Time',          value: (() => { const [h, m] = (form.appointment_time || '').split(':'); const hr = parseInt(h); return isNaN(hr) ? form.appointment_time : `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; })() },
                  { label: 'Ride Type',     value: form.ride_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
                  { label: 'Mobility Needs',value: [form.needs_wheelchair && 'Wheelchair', form.needs_walker && 'Walker', form.needs_stretcher && 'Stretcher'].filter(Boolean).join(', ') || 'None' },
                  { label: 'Special Instructions', value: form.special_instructions || 'None' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-sm font-semibold text-gray-500">{label}</span>
                    <span className="text-sm text-gray-800 text-right max-w-[55%]">{value}</span>
                  </div>
                ))}
              </div>
              {!user && (
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-xl text-sm text-purple-800">
                  💡 <strong>Tip:</strong> <Link to="/register" className="underline font-medium">Create an account</Link> to track your rides and receive notifications.
                </div>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8 pt-5 border-t border-gray-100">
            <button onClick={() => setStep(s => s - 1)} disabled={step === 0} className="btn-outline flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowLeft size={16} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => {
                if (step === 0 && (!form.passenger_name || !form.passenger_phone)) { toast.error('Please fill in required fields.'); return; }
                if (step === 1 && (!form.pickup_address || !form.destination_address || !form.appointment_date || !form.appointment_time)) { toast.error('Please fill in all trip details.'); return; }
                setStep(s => s + 1);
              }} className="btn-primary flex items-center gap-2">
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="btn-primary flex items-center gap-2 min-w-[140px] justify-center">
                {loading ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <CheckCircle size={16} />}
                {loading ? 'Submitting...' : 'Submit Booking'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
